import React from 'react';
import { useGetMoreLikeThisQuery, useGetStorefrontQuery } from '../../Api/Api';
import CAN, { PRICING } from '../Auth/Permissions';
import { parseProductPageConfig } from '../Helpers/productPageConfig';
import { useLocalization } from '../Helpers/useLocalization';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import ProductScroller from '../FeaturedProducts/ProductScroller';

interface IMoreLikeThisProps {
    productId?: string;
}

// "More Like This" row on the product page — similar-product cards from the moreLikeThis BFF endpoint
// (GET /products/{id}/moreLikeThis), gated by the storefront's enableMoreLikeThis setting. Rendered as
// a horizontal side-scroller (ProductScroller), matching the featured / "Recommended For You" rows, and
// reusing the productPageConfig moreLikeThis* style keys for the section/heading/card so it reads as
// part of the product page.
const MoreLikeThis: React.FC<IMoreLikeThisProps> = ({ productId }) => {
    const canSeePricing = CAN(PRICING);
    const L = useLocalization();
    const sfSettings = useGetStorefrontQuery();
    const enabled = sfSettings.data?.enableMoreLikeThis ?? false;
    // Skip the fetch entirely when the setting is off — no point calling a feature-flagged endpoint
    // the BFF will just return empty for anyway.
    const moreLikeThis = useGetMoreLikeThisQuery({ productId: productId ?? '' }, { skip: !productId || !enabled });

    const config = React.useMemo(
        () => parseProductPageConfig(sfSettings.data?.productPageConfig),
        [sfSettings.data?.productPageConfig]
    );

    if (!enabled || moreLikeThis.isLoading || moreLikeThis.error) return null;
    const data = moreLikeThis.data;
    if (!data || data.length === 0) return null;

    // The endpoint may not gate pricing itself — defensively strip it here too, same as LinkedProducts.
    const products = canSeePricing ? data : data.map((p) => ({ ...p, price: undefined }));

    return (
        <div style={{ marginTop: '2em', ...elementStyleFrom(config.moreLikeThisSection) }}>
            <h2 style={{ lineHeight: 1.3, marginBottom: '0.75em', ...elementStyleFrom(config.moreLikeThisHeading) }}>{L('localization_more_like_this_heading')}</h2>
            <ProductScroller
                products={products}
                showFeaturedFlag={false}
                cardStyle={elementStyleFrom(config.moreLikeThisCard)}
                scrollButtonStyle={elementStyleFrom(config.moreLikeThisScrollButton)}
                scrollbarStyle={elementStyleFrom(config.moreLikeThisScrollbar)}
                source="product"
            />
        </div>
    );
};

export default MoreLikeThis;
